// copy-register: commercial
import { Link } from "react-router-dom";
import { Glyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import type { EnterpriseMessage, IntegrationProofClass } from "@apf/framework-config";
import { useCopy, useFramework } from "../framework";
import { PageHeader, SectionHeading, ordinal } from "../components/ui";
import { integrationRecipePath, workblockSourceUrl } from "../catalogue";
import { useRevealOnce } from "./support";

function ProofMark({ proofClass }: { proofClass: IntegrationProofClass }) {
  const copy = useCopy();
  return <span className={`apf-proof-mark apf-proof-mark--${proofClass}`}>{copy(`page.connected-work.proof.${proofClass}`)}</span>;
}

const categoryGlyph: Record<EnterpriseMessage["category"], GlyphMeaning> = {
  commercial: "class.proof",
  "track-record": "capability.audit",
  governance: "capability.controls",
  integration: "capability.integration",
  interoperability: "capability.integration",
  residency: "capability.data",
  regulatory: "capability.controls",
  deployment: "foundation.execution-rails",
  accountability: "class.role"
};

/** Product-owner-approved enterprise message house for the Accenture site. */
export function WhyGroundworksPage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const reveal = useRevealOnce<HTMLOListElement>();
  const messages = "enterpriseMessages" in snapshot.content ? snapshot.content.enterpriseMessages ?? [] : [];

  return (
    <div className="apf-page apf-register-marketing apf-why-groundworks-page">
      <PageHeader
        eyebrow={copy("page.why-groundworks.eyebrow")}
        title={copy("page.why-groundworks.title")}
        description={copy("page.why-groundworks.description")}
      />

      <section className="apf-statement-section apf-statement-section--inverse apf-statement-section--gradient apf-page-section" aria-labelledby="why-groundworks-position">
        <p className="apf-statement-section__eyebrow">{copy("page.why-groundworks.position-label")}</p>
        <h2 className="apf-statement-section__headline" id="why-groundworks-position">
          {copy("page.why-groundworks.position-headline")}
        </h2>
        <div className="apf-statement-section__column">
          <p>{copy("page.why-groundworks.position")}</p>
        </div>
      </section>

      <section className="apf-page-section" aria-labelledby="why-groundworks-reasons">
        <SectionHeading
          id="why-groundworks-reasons"
          title={copy("page.why-groundworks.reasons-title")}
          description={copy("page.why-groundworks.reasons-description")}
        />
        <ol className={`apf-enterprise-message-list${reveal.revealClass}`} ref={reveal.ref}>
          {messages.map((message, index) => (
            <li className="apf-enterprise-message" id={message.id} key={message.id}>
              <div className="apf-enterprise-message__marker" aria-hidden="true">
                <span>{ordinal(index + 1)}</span>
                <Glyph meaning={categoryGlyph[message.category]} size={20} />
              </div>
              <div className="apf-enterprise-message__body">
                <p className="apf-enterprise-message__eyebrow">{copy(message.headlineKey)}</p>
                <h3>{copy(message.titleKey)}</h3>
                <div className="apf-enterprise-message__columns">
                  <div>
                    <p className="apf-enterprise-message__label">{copy("page.why-groundworks.value-label")}</p>
                    <p>{copy(message.buyerValueKey)}</p>
                  </div>
                  <div>
                    <p className="apf-enterprise-message__label">{copy("page.why-groundworks.mechanism-label")}</p>
                    <p>{copy(message.mechanismKey)}</p>
                  </div>
                </div>
                {message.integrationExampleIds?.length ? (
                  <div className="apf-enterprise-message__proofs">
                    <p className="apf-enterprise-message__label">{copy("page.why-groundworks.proof-recipes-label")}</p>
                    <ul>
                      {message.integrationExampleIds.flatMap((id) => {
                        const example = snapshot.integrations.examples.find((candidate) => candidate.id === id);
                        if (!example) return [];
                        const firstRecipeId = example.recipeIds[0];
                        return [<li key={example.id}>
                          <div><ProofMark proofClass={example.proofClass} /><strong>{example.label}</strong></div>
                          <p>{example.summary}</p>
                          {firstRecipeId ? <Link to={integrationRecipePath(firstRecipeId)}>{copy("page.why-groundworks.open-recipe")}</Link> : null}
                          {example.workblockPath ? <>{firstRecipeId ? " · " : null}<a href={workblockSourceUrl(example.workblockPath)} target="_blank" rel="noreferrer">{copy("page.connected-work.open-demo-workblock")}</a></> : null}
                        </li>];
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="apf-page-section apf-enterprise-close" aria-labelledby="why-groundworks-close">
        <SectionHeading id="why-groundworks-close" title={copy("page.why-groundworks.close-title")} />
        <p>{copy("page.why-groundworks.close-body")}</p>
        <Link to="/connected-work">
          {copy("page.why-groundworks.connected-work-link")}
        </Link>
      </section>
    </div>
  );
}
